/**
 * Arcade-style collision response for boundary and obstacle impacts.
 *
 * impactSeverity = abs(dot(normalisedVelocity, collisionNormal))
 *   0 = purely glancing (tangential)
 *   1 = purely head-on
 *
 * glancingThreshold — severity below this value receives no forward speed penalty
 *   (the reflection still happens; only the extra arcade braking is suppressed).
 *   Severity above the threshold is remapped to [0, 1] before applying headOnPenalty,
 *   so the onset is smooth rather than a cliff.
 *
 * dvx/dvz:       world-space velocity delta from the standard bounce reflection
 * fwdSpeedScale: multiply the post-reflection world velocity by this to add the
 *                extra arcade braking effect for significant impacts
 */
export interface ArcadeImpactResult {
  dvx: number;
  dvz: number;
  fwdSpeedScale: number;
}

/**
 * Compute arcade collision response for a single surface hit.
 *
 * @param velX              World-space X velocity at moment of impact
 * @param velZ              World-space Z velocity at moment of impact
 * @param nx                Collision normal X (unit vector, away from surface)
 * @param nz                Collision normal Z (unit vector, away from surface)
 * @param cor               Coefficient of restitution (0 = absorb, 1 = elastic)
 * @param headOnPenalty     Fraction of speed removed at a pure head-on hit (0–1)
 * @param glancingThreshold Severity below which no speed penalty is applied (0–1, default 0)
 */
export function arcadeImpact(
  velX: number,
  velZ: number,
  nx: number,
  nz: number,
  cor: number,
  headOnPenalty: number,
  glancingThreshold = 0,
): ArcadeImpactResult {
  const dot = velX * nx + velZ * nz;
  if (dot >= 0) return { dvx: 0, dvz: 0, fwdSpeedScale: 1 }; // separating — no response

  const speed = Math.sqrt(velX * velX + velZ * velZ);
  const impactSeverity = speed > 0.01 ? Math.abs(dot) / speed : 0;

  // Below the glancing threshold: pure deflection, no forward speed penalty.
  // Above: remap severity into [0, 1] over the above-threshold range so the
  // onset is gradual rather than a sudden cliff.
  let fwdSpeedScale = 1;
  if (glancingThreshold < 1 && impactSeverity > glancingThreshold) {
    const t = (impactSeverity - glancingThreshold) / (1 - glancingThreshold);
    fwdSpeedScale = Math.max(0, 1 - t * headOnPenalty);
  }

  return {
    dvx: -(1 + cor) * dot * nx,
    dvz: -(1 + cor) * dot * nz,
    fwdSpeedScale,
  };
}
